import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2 } from "lucide-react";
import { resetPassword } from "@/lib/identity/identity-api";
import { friendlyNetworkError } from "@/lib/http/network-error";

export function ResetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [token, setToken] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [loading, setLoading] = useState(false);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const t = searchParams.get("token")?.trim() || "";
    if (!t) {
      setError("Missing reset token. Open the link from your email, or request a new one.");
      setReady(false);
      return;
    }
    setToken(t);
    setReady(true);
  }, [searchParams]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setInfo("");
    if (password.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }
    if (password !== confirm) {
      setError("Passwords do not match.");
      return;
    }
    setLoading(true);
    try {
      await resetPassword(token, password);
      setInfo("Password updated. You can sign in.");
      setTimeout(() => navigate("/login"), 1500);
    } catch (err) {
      const apiMsg = err.response?.data?.error || err.response?.data?.detail;
      setError(
        typeof apiMsg === "string" && apiMsg.trim()
          ? apiMsg
          : friendlyNetworkError(err, "Could not reset password.")
      );
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-24" data-testid="reset-password-page">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="font-heading text-2xl text-center">Set new password</CardTitle>
        </CardHeader>
        <CardContent>
          {!ready ? (
            <div className="space-y-4 text-center">
              {error && <p className="text-sm text-destructive">{error}</p>}
              <Link to="/forgot-password" className="text-primary text-sm">
                Request a new link
              </Link>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <Input
                type="password"
                placeholder="New password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
                autoComplete="new-password"
              />
              <Input
                type="password"
                placeholder="Confirm password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                required
                minLength={6}
                autoComplete="new-password"
              />
              {error && <p className="text-sm text-destructive">{error}</p>}
              {info && <p className="text-sm text-muted-foreground">{info}</p>}
              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : "Update password"}
              </Button>
            </form>
          )}
          <p className="text-center text-sm mt-6">
            <Link to="/login" className="text-primary">
              Back to sign in
            </Link>
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
